import React from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Phone, MessageCircle, Clock, Loader2, Mic, MicOff } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface OutboundCallHistoryProps {
  contactId: string;
}

export function OutboundCallHistory({ contactId }: OutboundCallHistoryProps) {
  const { data: calls, isLoading } = useQuery({
    queryKey: ['outbound-call-history', contactId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_contact_calls_with_handler')
        .select('id, started_at, duration_seconds, status, direction, handled_by_display_name')
        .eq('contact_id', contactId)
        .order('started_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60 * 1000,
  });

  const callIds = (calls ?? []).map((c) => c.id).filter(Boolean) as string[];

  const { data: recordedCallIds } = useQuery({
    queryKey: ['outbound-call-recordings', contactId, callIds.join(',')],
    enabled: callIds.length > 0,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('call_recordings')
        .select('call_id')
        .in('call_id', callIds);
      if (error) throw error;
      return new Set((data ?? []).map((r) => r.call_id));
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm py-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        Carregando histórico...
      </div>
    );
  }

  if (!calls || calls.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-2">Nenhum contato registrado.</p>
    );
  }

  return (
    <div className="space-y-2 pt-1">
      {calls.map((call) => {
        const duration = call.duration_seconds
          ? `${Math.floor(call.duration_seconds / 60)}m ${call.duration_seconds % 60}s`
          : null;
        const date = call.started_at
          ? safeFormat(new Date(call.started_at), "dd/MM/yy 'às' HH:mm", { locale: ptBR })
          : '—';
        const ago = call.started_at
          ? formatDistanceToNow(new Date(call.started_at), { locale: ptBR, addSuffix: true })
          : '';

        return (
          <div
            key={call.id}
            className="flex items-start gap-2 text-sm border-l-2 border-muted pl-3 py-1"
          >
            <Phone className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{date}</span>
                <span className="text-muted-foreground text-xs">{ago}</span>
              </div>
              <div className="text-muted-foreground text-xs flex items-center gap-2 flex-wrap">
                {call.handled_by_display_name && (
                  <span>por {call.handled_by_display_name}</span>
                )}
                {duration && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {duration}
                  </span>
                )}
                <span
                  className={
                    call.status === 'completed'
                      ? 'text-green-600'
                      : call.status === 'no-answer'
                      ? 'text-yellow-600'
                      : 'text-muted-foreground'
                  }
                >
                  {call.status === 'completed'
                    ? 'Atendida'
                    : call.status === 'no-answer'
                    ? 'Sem resposta'
                    : call.status ?? 'Desconhecido'}
                </span>
                {recordedCallIds?.has(call.id) ? (
                  <span className="flex items-center gap-1 text-primary">
                    <Mic className="h-3 w-3" />
                    Gravada
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <MicOff className="h-3 w-3" />
                    Sem gravação
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
